import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/layout/PageHeader';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';

interface ModelMappingRow {
  id: string;
  sourceModel: string;
  targetModel: string;
  isDefault: boolean;
  inputPrice: number;
  outputPrice: number;
  createdAt: string;
  updatedAt: string;
}

export default function AdminModelMappingsPage() {
  const { t } = useTranslation(['adminModelMappings', 'common']);
  const [mappings, setMappings] = useState<ModelMappingRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [sourceInput, setSourceInput] = useState('');
  const [targetInput, setTargetInput] = useState('');
  const [inputPriceInput, setInputPriceInput] = useState(1.4);
  const [outputPriceInput, setOutputPriceInput] = useState(4.4);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  useEffect(() => {
    fetchMappings();
  }, []);

  const fetchMappings = async () => {
    try {
      const res = await fetch('/api/admin/model-mappings', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMappings(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch model mappings:', error);
      toast.error(t('adminModelMappings:errors.load'));
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingId(null);
    setSourceInput('');
    setTargetInput('');
    setInputPriceInput(1.4);
    setOutputPriceInput(4.4);
    setShowModal(true);
  };

  const openEditModal = (mapping: ModelMappingRow) => {
    setEditingId(mapping.id);
    setSourceInput(mapping.sourceModel);
    setTargetInput(mapping.targetModel);
    setInputPriceInput(Number(mapping.inputPrice));
    setOutputPriceInput(Number(mapping.outputPrice));
    setShowModal(true);
  };

  const saveMapping = async () => {
    if (!sourceInput.trim() || !targetInput.trim()) {
      toast.error(t('adminModelMappings:errors.requiredFields'));
      return;
    }

    try {
      const url = editingId
        ? `/api/admin/model-mappings/${editingId}`
        : '/api/admin/model-mappings';
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sourceModel: sourceInput.trim(),
          targetModel: targetInput.trim(),
          inputPrice: inputPriceInput,
          outputPrice: outputPriceInput,
        }),
      });

      if (res.ok) {
        toast.success(editingId ? t('adminModelMappings:messages.updated') : t('adminModelMappings:messages.created'));
        setShowModal(false);
        fetchMappings();
      } else {
        const data = await res.json();
        toast.error(data.error || t('adminModelMappings:errors.save'));
      }
    } catch (error) {
      console.error('Failed to save mapping:', error);
      toast.error(t('adminModelMappings:errors.save'));
    }
  };

  const deleteMapping = async () => {
    if (!deleteId) return;

    try {
      const res = await fetch(`/api/admin/model-mappings/${deleteId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (res.ok) {
        toast.success(t('adminModelMappings:messages.deleted'));
        setShowDeleteModal(false);
        setDeleteId(null);
        fetchMappings();
      } else {
        const data = await res.json();
        toast.error(data.error || t('adminModelMappings:errors.delete'));
      }
    } catch (error) {
      console.error('Failed to delete mapping:', error);
      toast.error(t('adminModelMappings:errors.delete'));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('adminModelMappings:title')} description={t('adminModelMappings:description')} />
        <div className="space-y-2">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-14 rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader title={t('adminModelMappings:title')} description={t('adminModelMappings:description')} />
        <Button onClick={openCreateModal} size="sm">
          <Plus className="h-4 w-4 mr-1" />
          {t('adminModelMappings:actions.add')}
        </Button>
      </div>

      <div className="bg-muted rounded-lg p-4 text-sm text-muted-foreground">
        <p>{t('adminModelMappings:help.requestRouting')} <code>claude-sonnet-4-20250514</code>{t('adminModelMappings:help.requestRoutingSuffix')}</p>
        <p className="mt-1">{t('adminModelMappings:help.defaultMappingPrefix')} <strong>{t('adminModelMappings:help.defaultMappingLabel')}</strong> (<code>*</code>) {t('adminModelMappings:help.defaultMappingSuffix')}</p>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('adminModelMappings:table.sourceModel')}</TableHead>
              <TableHead>{t('adminModelMappings:table.targetModel')}</TableHead>
              <TableHead className="text-right">{t('adminModelMappings:table.inputPrice')}</TableHead>
              <TableHead className="text-right">{t('adminModelMappings:table.outputPrice')}</TableHead>
              <TableHead>{t('adminModelMappings:table.type')}</TableHead>
              <TableHead className="text-right">{t('adminModelMappings:table.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mappings.map((mapping) => (
              <TableRow key={mapping.id}>
                <TableCell>
                  <code className="text-sm bg-muted px-2 py-0.5 rounded">{mapping.sourceModel}</code>
                </TableCell>
                <TableCell>
                  <code className="text-sm bg-muted px-2 py-0.5 rounded">{mapping.targetModel}</code>
                </TableCell>
                <TableCell className="text-right text-sm">${Number(mapping.inputPrice).toFixed(2)}</TableCell>
                <TableCell className="text-right text-sm">${Number(mapping.outputPrice).toFixed(2)}</TableCell>
                <TableCell>
                  {mapping.isDefault ? (
                    <Badge variant="default">{t('adminModelMappings:table.default')}</Badge>
                  ) : (
                    <Badge variant="secondary">{t('adminModelMappings:table.custom')}</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" aria-label={t('adminModelMappings:actions.edit')} onClick={() => openEditModal(mapping)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {!mapping.isDefault && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        aria-label={t('adminModelMappings:actions.delete')}
                        onClick={() => { setDeleteId(mapping.id); setShowDeleteModal(true); }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? t('adminModelMappings:dialog.editTitle') : t('adminModelMappings:dialog.addTitle')}</DialogTitle>
            <DialogDescription>
              {editingId
                ? t('adminModelMappings:dialog.editDescription')
                : t('adminModelMappings:dialog.addDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="sourceModel">{t('adminModelMappings:form.sourceModel')}</Label>
              <Input
                id="sourceModel"
                value={sourceInput}
                onChange={(e) => setSourceInput(e.target.value)}
                placeholder={t('adminModelMappings:form.sourcePlaceholder')}
                disabled={editingId !== null && mappings.find(m => m.id === editingId)?.isDefault === true}
              />
              <p className="text-xs text-muted-foreground">
                {t('adminModelMappings:form.sourceHelpPrefix')} <code>*</code> {t('adminModelMappings:form.sourceHelpSuffix')}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="targetModel">{t('adminModelMappings:form.targetModel')}</Label>
              <Input
                id="targetModel"
                value={targetInput}
                onChange={(e) => setTargetInput(e.target.value)}
                placeholder={t('adminModelMappings:form.targetPlaceholder')}
              />
              <p className="text-xs text-muted-foreground">
                {t('adminModelMappings:form.targetHelp')}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="inputPrice">{t('adminModelMappings:form.inputPrice')}</Label>
                <Input
                  id="inputPrice"
                  type="number"
                  min="0"
                  step="0.01"
                  value={inputPriceInput}
                  onChange={(e) => setInputPriceInput(parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="outputPrice">{t('adminModelMappings:form.outputPrice')}</Label>
                <Input
                  id="outputPrice"
                  type="number"
                  min="0"
                  step="0.01"
                  value={outputPriceInput}
                  onChange={(e) => setOutputPriceInput(parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>{t('common:cancel')}</Button>
            <Button onClick={saveMapping}>{t('common:save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={showDeleteModal}
        onOpenChange={(open) => { setShowDeleteModal(open); if (!open) setDeleteId(null); }}
        title={t('adminModelMappings:deleteDialog.title')}
        description={t('adminModelMappings:deleteDialog.description')}
        confirmLabel={t('adminModelMappings:actions.delete')}
        cancelLabel={t('common:cancel')}
        variant="destructive"
        onConfirm={deleteMapping}
      />
    </div>
  );
}
